import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Login,
  Language,
  Update,
  Delete,
  Add,
  Person,
  Security,
  Error as ErrorIcon,
  Warning,
  Info,
  FilterList,
  Refresh,
  Download,
  Search
} from '@mui/icons-material';
import api from '../services/api';

const Logs = () => {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    level: '',
    activity_type: '',
    status: '',
    search: '',
  });
  const [stats, setStats] = useState({
    total_activities: 0,
    total_system_logs: 0,
    errors_today: 0,
    activities_today: 0
  });

  const itemsPerPage = 20;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) =>
            value !== null && value !== undefined && value !== ''
          )
        )
      });

      const response = await api.get(`/sites/nerv/all-logs?${params}`);
      setLogs(response.data);
      setStats(response.data.summary || {});
    } catch (error) {
      setError('Failed to load logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters, itemsPerPage]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      level: '',
      activity_type: '',
      status: '',
      search: '',
      date_from: null,
      date_to: null
    });
    setPage(1);
  };

  const getActivityIcon = (activityType) => {
    const iconMap = {
      'login': <Login />,
      'logout': <Person />,
      'site_create': <Add />,
      'site_update': <Update />,
      'site_delete': <Delete />,
      'plugin_check': <Security />,
      'test': <Info />
    };
    return iconMap[activityType] || <Info />;
  };

  const getSystemLogIcon = (level) => {
    const iconMap = {
      'error': <ErrorIcon />,
      'critical': <ErrorIcon />,
      'warning': <Warning />,
      'info': <Info />
    };
    return iconMap[level] || <Info />;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'success': 'success',
      'failure': 'error',
      'error': 'error'
    };
    return colorMap[status] || 'default';
  };

  const getLogLevelColor = (level) => {
    const colorMap = {
      'error': 'error',
      'critical': 'error',
      'warning': 'warning',
      'info': 'info'
    };
    return colorMap[level] || 'default';
  };

  const formatTimeago = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMins < 1) return 'Just now';
    if (diffInMins < 60) return `${diffInMins}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const { recent_activities = [], recent_system_logs = [], pagination = {} } = logs || {};
  const allLogs = [...recent_activities, ...recent_system_logs].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const totalPages = Math.ceil((pagination.total || 0) / itemsPerPage);

  return (
    <Box sx={{ py: 4 }}>
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 1
              }}
            >
              System Logs
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              Comprehensive platform activity and system logs
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <Info />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.activities_today || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Activities Today
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'error.main' }}>
                      <ErrorIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.errors_today || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Errors Today
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <Security />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.total_activities || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Activities
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <Language />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.total_system_logs || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        System Logs
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', mb: 4 }}>
            <CardHeader
              title="Filters"
              subheader="Filter logs by various criteria"
              action={
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Clear all filters">
                    <IconButton onClick={clearFilters}>
                      <FilterList />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh logs">
                    <IconButton onClick={fetchLogs}>
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Level</InputLabel>
                    <Select
                      value={filters.level}
                      onChange={(e) => handleFilterChange('level', e.target.value)}
                      label="Level"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="info">Info</MenuItem>
                      <MenuItem value="warning">Warning</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Activity Type</InputLabel>
                    <Select
                      value={filters.activity_type}
                      onChange={(e) => handleFilterChange('activity_type', e.target.value)}
                      label="Activity Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="login">Login</MenuItem>
                      <MenuItem value="logout">Logout</MenuItem>
                      <MenuItem value="site_create">Site Create</MenuItem>
                      <MenuItem value="site_update">Site Update</MenuItem>
                      <MenuItem value="site_delete">Site Delete</MenuItem>
                      <MenuItem value="plugin_check">Plugin Check</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="failure">Failure</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    placeholder="Search logs..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Logs List */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
            <CardHeader
              title="All Logs"
              subheader={`Showing ${allLogs.length} of ${pagination.total || 0} logs`}
              action={
                <Button
                  startIcon={<Download />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    // TODO: Implement log export functionality
                  }}
                >
                  Export
                </Button>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : allLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No logs found matching your criteria
                </Typography>
              ) : (
                <>
                  <List>
                    {allLogs.map((log, index) => {
                      const isActivity = 'activity_type' in log;
                      const isSystemLog = 'level' in log;

                      return (
                        <React.Fragment key={`${isActivity ? 'activity' : 'system'}-${log.id}`}>
                          <ListItem sx={{ px: 0, py: 2 }}>
                            <ListItemIcon sx={{ minWidth: 50 }}>
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: isActivity
                                    ? (getStatusColor(log.status) === 'success' ? 'success.main' :
                                       getStatusColor(log.status) === 'error' ? 'error.main' : 'primary.main')
                                    : (getLogLevelColor(log.level) === 'error' ? 'error.main' :
                                       getLogLevelColor(log.level) === 'warning' ? 'warning.main' : 'info.main'),
                                  fontSize: '1.2rem'
                                }}
                              >
                                {isActivity ? getActivityIcon(log.activity_type) : getSystemLogIcon(log.level)}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {isActivity ? log.description : log.message}
                                  </Typography>
                                  <Chip
                                    label={isActivity ? log.status : log.level.toUpperCase()}
                                    size="small"
                                    color={isActivity ? getStatusColor(log.status) : getLogLevelColor(log.level)}
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                  {isActivity && (
                                    <Chip
                                      label={log.activity_type.replace('_', ' ').toUpperCase()}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    {isActivity && (
                                      <>
                                        <Typography variant="body2" color="text.secondary">
                                          User: {log.user_name || 'System'}
                                        </Typography>
                                        {log.target_name && (
                                          <>
                                            <Typography variant="body2" color="text.secondary">•</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              Target: {log.target_name}
                                            </Typography>
                                          </>
                                        )}
                                      </>
                                    )}
                                    {isSystemLog && log.module && (
                                      <Typography variant="body2" color="text.secondary">
                                        Module: {log.module}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatFullDate(log.created_at)} ({formatTimeago(log.created_at)})
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < allLogs.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, newPage) => setPage(newPage)}
                        color="primary"
                        size="large"
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>
  );
};

export default Logs;